import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import {
  Button,
  Divider
} from '@material-ui/core';
import { BackdropCircular, PaymentOption, StoreAddons } from 'components';
import {
  getSubscriptionSetupApi,
  saveUserOnboardingDataApi,
  saveMarketingDataApi,
  billStoreFromOnboardingApi,
  getAdditionalSkusApi,
  getAdditionalSpecialsApi,
  getAdditionalFeaturesApi
} from 'api';
import { changeOnboardingData } from 'redux/actions/subscription';
import { states } from 'helpers/usStates';
import { validationSchema } from './validation';
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import StepFour from './StepFour';
import './steps.css';


const steps = [
  {
    title: 'Store Information',
    description: 'Account setup'
  },
  {
    title: 'Base Store Setup',
    description: 'Base store setup'
  },
  {
    title: 'Store Add-Ons',
    description: 'Store Add-Ons'
  },
  {
    title: 'Payment Options',
    description: 'Payment Options'
  },
  {
    title: 'Billing Information',
    description: 'Payment setup'
  },
  {
    title: 'Purchase',
    description: 'Review and purchase'
  }
];


export default () => {
  const dispatch = useDispatch();
  const {
    userProfile: {onboarding = {}, marketing = {}},
    subscriptionSetup,
    isLoadingAdditionalSkus,
    isLoadingAdditionalSpecials,
    isLoadingAdditionalFeatures,
    isLoadingMarketignData,
    isLoadingMarketingBilling,
    isLoadingSubscriptionBenefits
  } = useSelector((state) => state.subscription);
  const {featureIds, skuIds, specialIds, plan, selectedBenefit } = marketing;

  const { loadingProfile, savingOnboardingData } = useSelector((state) => state.user);
  const { activeStep = 1 } = onboarding;
  const loading = loadingProfile || savingOnboardingData || isLoadingAdditionalSkus ||
    isLoadingAdditionalSpecials || isLoadingAdditionalFeatures || isLoadingMarketignData ||
    isLoadingMarketingBilling || isLoadingSubscriptionBenefits;


  useEffect(() => {
    getSubscriptionSetupApi({dispatch});
    getAdditionalSkusApi({dispatch});
    getAdditionalSpecialsApi({dispatch});
    getAdditionalFeaturesApi({dispatch});
  }, []);

  const handleClickNext = async (values) => {
    const [ccExpMonth, ccExpYear] = values.billingInformation.expiry.split('/');
    const setupData = {
      ...onboarding,
      ...values,
      billingInformation: {...values.billingInformation, ccExpMonth, ccExpYear},
      activeStep
    };

    const saveSuccess = await saveUserOnboardingDataApi({dispatch, setupData});

    if (!saveSuccess) {
      return;
    }

    if (activeStep === 3 || activeStep === 4) {
      const marketingSummary = {
        featureIds,
        skuIds,
        specialIds,
        plan: plan || 'annually',
        selectedBenefit
      };

      saveMarketingDataApi({dispatch, marketingSummary});
    }
  };

  const handleBack = (setFieldValue) => {
    const newActiveStep = activeStep - 1;
    dispatch(changeOnboardingData({...onboarding, activeStep: newActiveStep }));
    setFieldValue('activeStep', newActiveStep);
  };

  // TODO: refactor this validation
  const isValidToGoNext = (values, touched, errors) => {
    if (activeStep === 1) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {website, streetAddress2, ...requiredValues} = values.storeInformation;
      const isContainEmptyValues = Object.values(requiredValues).includes('');
      return isContainEmptyValues && (!touched?.storeInformation || !!errors.storeInformation);
    }

    if (activeStep === 5) {
      const haveErrors =  Boolean(Object.keys(errors?.billingInformation || {}).length);

      return !values.billingInformation?.ccNumber || haveErrors;
    }

    if (activeStep === 6) {
      return !touched?.billingInformation || !!errors.billingInformation;
    }

    return false;
  };

  return (
    <div
      className="bg-white flex-grow flex-column relative h-full scroll-y"
    >
      <div className="arrow-steps">
        {steps.map((step, index) => (
          <div
            key={`step-${step.title}`}
            className={`btn btn-arrow
              ${activeStep === (index + 1) ? 'current' : ''}
              ${activeStep > (index + 1) ? 'done' : ''}
            `}
          >
            <div className="title">{step.title}</div>
            <div>{step.description}</div>
          </div>
        ))}
      </div>
      {Boolean(onboarding?.storeInformation) && Boolean(subscriptionSetup?._id) &&
        <Formik
          initialValues={{
            storeInformation: {
              firstName: onboarding?.storeInformation?.firstName || '',
              lastName: onboarding?.storeInformation?.lastName || '',
              storeName: onboarding?.storeInformation?.storeName || '',
              website: onboarding?.storeInformation?.website || '',
              streetAddress1: onboarding?.storeInformation?.streetAddress1 || '',
              streetAddress2: onboarding?.storeInformation?.streetAddress2 || '',
              city: onboarding?.storeInformation?.city || '',
              state: onboarding?.storeInformation?.state || '',
              stateId: onboarding?.storeInformation?.stateId || '',
              zipCode: onboarding?.storeInformation?.zipCode || '',
              country: 'US',
              timezone: onboarding?.storeInformation?.timezone || 'America/Chicago',
              phone: onboarding?.storeInformation?.phone || ''
            },
            billingInformation: {
              firstName: '',
              lastName: '',
              ccNumber: '',
              ccExpMonth: '',
              ccExpYear: '',
              ccCVV: '',
              streetAddress1: '',
              streetAddress2: '',
              city: '',
              zipCode: '',
              country: 'US',
              state: '',
              phone: '',
              company: '',
              expiry: ''
            },
            activeStep,
            selectedDiscountsIds: onboarding?.selectedDiscountsIds || [],
            totalOneTimePrice: onboarding?.totalOneTimePrice || 0,
            totalMonthlyPrice: onboarding?.totalMonthlyPrice || 0,
            isTermsAccepted: false
          }}
          onSubmit={(values) => {
            const {
              billingInformation: {
                firstName,
                lastName,
                state: regionId,
                ccNumber,
                ccCVV,
                streetAddress1,
                streetAddress2,
                city,
                zipCode,
                phone,
                company,
                expiry
              }
            } = values;
            const [ccExpMonth, ccExpYear] = expiry.split('/');
            const {shortName: regionCode, name: regionName} = states.find((s) => s.value === regionId);
            const billData = {
              firstName,
              lastName,
              regionCode,
              regionName,
              regionId,
              countryId: 'US',
              street: [streetAddress1, streetAddress2],
              telephone: phone,
              postcode: zipCode,
              city,
              company,
              ccNum: ccNumber,
              ccExpYear,
              ccExpMonth,
              cvv: ccCVV,
              replaceForSubscriptions: false
            };

            billStoreFromOnboardingApi({dispatch, billData});
          }}
          validationSchema={validationSchema}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            handleChange,
            handleBlur,
            setFieldValue
          }) => (
            <form
              noValidate
              onSubmit={handleSubmit}
              className="px-8 py-18 text-primary-gray"
            >
              <div>
                {activeStep === 1 &&
                <StepOne
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                />}
                {activeStep === 2 &&
                  <StepTwo
                    values={values}
                    storePriceSettings={subscriptionSetup}
                    setFieldValue={setFieldValue}
                  />}
                {activeStep === 3 && <StoreAddons />}
                {activeStep === 4 && <div className="flex"><PaymentOption /></div>}
                {activeStep === 5 &&
                <StepThree
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                />}
                {activeStep === 6 &&
                <StepFour
                  values={values}
                  handleChange={handleChange}
                />}
              </div>
              <Divider className="my-8" />
              <div className="flex justify-end">
                {activeStep != 1 &&
                <Button
                  onClick={() => handleBack(setFieldValue)}
                  className="mr-2"
                  variant="outlined"
                >
                  Previous
                </Button>
                }
                {activeStep == steps.length ?
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={!values.isTermsAccepted}
                  >
                    Purchase and Setup your Store
                  </Button>
                  :
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleClickNext(values)}
                    disabled={isValidToGoNext(values, touched, errors)}
                  >
                    {activeStep === 6 ? 'Review' : 'Next'}
                  </Button>
                }
              </div>
            </form>
          )}
        </Formik>
      }
      <BackdropCircular loading={loading} />
    </div>
  );
};
